import React from "react";

import Slider from "rc-slider";
import { useTranslation } from "react-i18next";

import SwitchOnOff from "../../../../components/common/tools/SwitchOnOff";
import { COMPONENT_TYPES } from "../../../../constants/componentType";
import {
  ADDITIONNAL_IMAGE_FORMATS,
  DEFAULT_IMAGE_FORMATS,
} from "../../../../constants/ratio";
import { editor_primary_color } from "../../../../utils/Utilities";
import ImageFormatSelector from "../ImageFormatSelector";

import type { JSX } from "react";

import type { CarouselComponent } from "../../../../api/ProjectAPI.types";
import type { ComponentType } from "../../../../constants/componentType";
import type { ImageFormat } from "../../../../constants/ratio";

interface ContentCollectionComponentProps {
  component: CarouselComponent;
  onChangeOption: (args: {
    type: ComponentType;
    value: string | number | boolean;
    key: keyof CarouselComponent["params"];
    staticComponentName: string;
  }) => void;
  onChangeEnabled: (args: {
    value: boolean;
    type: ComponentType;
    staticComponentName: string;
  }) => void;
}

function ContentCollectionComponent({
  component,
  onChangeOption,
  onChangeEnabled,
}: ContentCollectionComponentProps): JSX.Element {
  const { CAROUSEL } = COMPONENT_TYPES;

  const { t } = useTranslation("editor-platform");

  const marks = {
    1: "1",
    2: "2",
    3: "3",
    4: "4",
    5: "5",
    6: "6",
    7: "7",
    8: "8",
    9: "9",
    10: "10",
  };
  return (
    <div className="editor-form-section section-separator">
      <div className="editor-form-section-title">{t("collectionsLabel")}</div>
      <div className="editor-form-row">
        <div className="editor-form-row editor-form-row-inline">
          <div className="editor-form-input">
            <SwitchOnOff
              checked={component.enabled}
              name="activate_collections"
              size="sm"
              onChange={(value: boolean): void => {
                onChangeEnabled({
                  type: CAROUSEL,
                  staticComponentName: "collections_contents",
                  value,
                });
              }}
            />
          </div>
          <div className="editor-form-label">{t("activateCollections")}</div>
        </div>
      </div>
      {component.enabled ? (
        <React.Fragment>
          <div className="pb-[0.625rem]">
            <ImageFormatSelector
              selected={component.params.ratio}
              title={t("pictureRatio")}
              imageFormatList={[
                ...DEFAULT_IMAGE_FORMATS,
                ...ADDITIONNAL_IMAGE_FORMATS,
              ]}
              onChangeRatio={(ratio: ImageFormat): void => {
                onChangeOption({
                  type: CAROUSEL,
                  staticComponentName: "collections_contents",
                  key: "ratio",
                  value: ratio,
                });
              }}
            />
          </div>
          <div className="editor-form-row editor-form-row-inline">
            <div className="editor-form-input">
              <SwitchOnOff
                checked={component.params.display_title}
                name="display_title-collections"
                size="sm"
                onChange={(value: boolean): void => {
                  onChangeOption({
                    type: CAROUSEL,
                    staticComponentName: "collections_contents",
                    key: "display_title",
                    value,
                  });
                }}
              />
            </div>
            <div className="editor-form-label">{t("showComponentTitle")}</div>
          </div>
          <div className="editor-form-row editor-form-row-inline">
            <div className="editor-form-input">
              <SwitchOnOff
                checked={component.params.display_item_title}
                name="display_item_title-collections"
                size="sm"
                onChange={(value: boolean): void => {
                  onChangeOption({
                    type: CAROUSEL,
                    staticComponentName: "collections_contents",
                    key: "display_item_title",
                    value,
                  });
                }}
              />
            </div>
            <div className="editor-form-label">{t("showItemsTitle")}</div>
          </div>
          <div className="editor-form-row editor-form-row-inline">
            <div className="editor-form-input">
              <SwitchOnOff
                checked={component.params.display_castings}
                name="display_castings-collections"
                size="sm"
                onChange={(value: boolean): void => {
                  onChangeOption({
                    type: CAROUSEL,
                    staticComponentName: "collections_contents",
                    key: "display_castings",
                    value,
                  });
                }}
              />
            </div>
            <div className="editor-form-label">{t("showCurators")}</div>
          </div>
          <div className="editor-form-row editor-form-row-inline">
            <div className="editor-form-input">
              <SwitchOnOff
                checked={component.params.display_dialog}
                name="display_dialog-collections"
                size="sm"
                onChange={(value: boolean): void => {
                  onChangeOption({
                    type: CAROUSEL,
                    staticComponentName: "collections_contents",
                    key: "display_dialog",
                    value,
                  });
                }}
              />
            </div>
            <div className="editor-form-label">{t("showDialog")}</div>
          </div>
          <div className="editor-form-row editor-form-row-inline">
            <div className="editor-form-input">
              <SwitchOnOff
                checked={component.params.circular}
                name="circular-collections"
                size="sm"
                onChange={(value: boolean): void => {
                  onChangeOption({
                    type: CAROUSEL,
                    staticComponentName: "collections_contents",
                    key: "circular",
                    value,
                  });
                }}
              />
            </div>
            <div className="editor-form-label">{t("showCircular")}</div>
          </div>
          <div className="editor-form-row editor-form-row-inline">
            <div className="editor-form-input">
              <SwitchOnOff
                checked={component.params.arrows_on_hover}
                name="arrows_on_hover-collections"
                size="sm"
                onChange={(value: boolean): void => {
                  onChangeOption({
                    type: CAROUSEL,
                    staticComponentName: "collections_contents",
                    key: "arrows_on_hover",
                    value,
                  });
                }}
              />
            </div>
            <div className="editor-form-label">{t("showArrowHover")}</div>
          </div>
          <div className="editor-form-row m-t-10">
            <div className="editor-form-label">{t("componentPerSlides")}</div>
            <div className="editor-form-input">
              <Slider
                dots
                defaultValue={component.params.component_per_slides || 6}
                marks={marks}
                max={10}
                min={1}
                step={1}
                activeDotStyle={{
                  borderColor: editor_primary_color,
                  bottom: -6,
                  width: 12,
                  height: 12,
                  marginLeft: -6,
                }}
                dotStyle={{
                  bottom: -6,
                  width: 12,
                  height: 12,
                  marginLeft: -6,
                }}
                handleStyle={{
                  borderColor: editor_primary_color,
                  height: 20,
                  width: 20,
                  marginLeft: 0,
                  marginTop: -6,
                }}
                onChange={(value: number): void => {
                  onChangeOption({
                    type: CAROUSEL,
                    staticComponentName: "collections_contents",
                    key: "component_per_slides",
                    value,
                  });
                }}
                railStyle={{
                  backgroundColor: "var(--surface-muted)",
                  height: 8,
                }}
                trackStyle={{
                  backgroundColor: editor_primary_color,
                  height: 8,
                }}
              />
            </div>
          </div>
        </React.Fragment>
      ) : null}
    </div>
  );
}
export default ContentCollectionComponent;
